import './VideoUploadNavIndicator.css';

import React, { useEffect, useRef, useState } from 'react';

import { useVideoUploads } from '../../../contexts/VideoUploadContext';
import SpinnerIcon from '../SpinnerIcon';
import { groupByAthlete } from './groupByAthlete';


const VideoUploadNavIndicator: React.FC = () => {
  const { entries, clearEntry } = useVideoUploads();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const uploading = entries.filter((e) => e.status === 'uploading');
  const errored = entries.filter((e) => e.status === 'error');
  const hasAny = uploading.length > 0 || errored.length > 0;

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent): void => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  // Auto-close once everything finishes.
  useEffect(() => {
    if (!hasAny) setOpen(false);
  }, [hasAny]);

  if (!hasAny) return null;

  const showError = errored.length > 0;
  const groups = groupByAthlete(entries);

  return (
    <div className="video-upload-nav-indicator-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className={`video-upload-nav-indicator${showError ? ' video-upload-nav-indicator--error' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={
          showError
            ? `${errored.length} video upload${errored.length === 1 ? '' : 's'} failed`
            : `${uploading.length} video upload${uploading.length === 1 ? '' : 's'} in progress`
        }
      >
        {showError ? (
          <span className="video-upload-nav-indicator__label">Video Uploads Failed</span>
        ) : (
          <>
            <span className="video-upload-nav-indicator__label">
              Videos Uploading: {uploading.length}
            </span>
            <SpinnerIcon className="video-upload-nav-indicator__spinner" size={18} />
          </>
        )}
      </button>

      {open ? (
        <div className="video-upload-nav-indicator__dropdown" role="menu">
          <ul className="video-upload-nav-indicator__list">
            {groups.map((g) => {
              const text = g.failed > 0
                ? `${g.athleteName}: ${g.failed} failed`
                : `${g.athleteName}: ${g.uploading}`;
              const isErrorRow = g.failed > 0;
              return (
                <li
                  key={g.athleteId}
                  className={`video-upload-nav-indicator__row${isErrorRow ? ' video-upload-nav-indicator__row--error' : ''}`}
                >
                  {text}
                </li>
              );
            })}
          </ul>
          {showError ? (
            <div className="video-upload-nav-indicator__footer">
              <button
                type="button"
                className="video-upload-nav-indicator__dismiss"
                onClick={() => {
                  errored.forEach((e) => clearEntry(e.key));
                  setOpen(false);
                }}
              >
                Dismiss failed
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default VideoUploadNavIndicator;
